import React, { useState, useEffect, useMemo } from 'react';
import api, { inventoryAPI, productsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  Loader2,
  Boxes,
  ArrowDownToLine,
  ArrowUpFromLine,
  RefreshCw,
  Package,
  ChevronDown,
  ChevronRight,
  History,
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-LK', {
    style: 'currency',
    currency: 'LKR',
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (value) => {
  if (!value) return '—';
  const dateOnly = String(value).slice(0, 10);
  const [year, month, day] = dateOnly.split('-').map(Number);
  if (!year || !month || !day) return dateOnly;
  return new Date(year, month - 1, day).toLocaleDateString();
};

const batchStatus = (group) => {
  if (group.onHand > 0 && (group.consumed > 0 || group.returned > 0)) return 'Partially Used';
  if (group.onHand > 0) return 'Available';
  if (group.returned >= group.received && group.received > 0) return 'Returned';
  if (group.consumed > 0) return 'Consumed';
  return 'Closed';
};

const batchStatusClass = (status) => {
  if (status === 'Available') return 'badge-success';
  if (status === 'Partially Used') return 'badge-warning';
  if (status === 'Returned') return 'badge-error';
  return 'badge-neutral';
};

export const Inventory = () => {
  const [movements, setMovements] = useState([]);
  const [valuation, setValuation] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [reconciliation, setReconciliation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [variations, setVariations] = useState([]);
  const [variationsLoading, setVariationsLoading] = useState(false);
  const [writeoffReference, setWriteoffReference] = useState(() => crypto.randomUUID());
  const [showBatchHistory, setShowBatchHistory] = useState(false);
  const [expandedBatchGroups, setExpandedBatchGroups] = useState({});

  const [formData, setFormData] = useState({
    product_id: '',
    variation_id: '',
    movement_type: 'in',
    quantity: '',
    reason: '',
    unit_cost: '',
  });

  const selectedProduct = products.find((p) => p.id === formData.product_id);
  useEffect(() => {
    let active = true;
    setVariations([]);
    if (formData.movement_type !== 'promotion' || selectedProduct?.product_type !== 'variable') {
      setVariationsLoading(false);
      return () => { active = false; };
    }
    setVariationsLoading(true);
    productsAPI.getVariations(formData.product_id).then(({ data }) => {
      if (active) setVariations(data.variations || []);
    }).catch(() => { if (active) toast.error('Could not load product sizes. Select the product again.'); })
      .finally(() => { if (active) setVariationsLoading(false); });
    return () => { active = false; };
  }, [formData.product_id, formData.movement_type, selectedProduct?.product_type]);

  const batchGroups = useMemo(() => {
    const grouped = new Map();
    for (const batch of batches) {
      const source = batch.grn_id || batch.grn_number || batch.source_reference || batch.source_type || 'manual';
      const unitCost = Number(batch.unit_cost || 0);
      const key = `${source}::${batch.product_id || ''}::${unitCost.toFixed(4)}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          id: key,
          source,
          grnNumber: batch.grn_number || batch.source_reference || batch.source_type || 'Manual',
          productId: batch.product_id,
          productName: batch.product_name || 'Product',
          receivedDate: batch.received_date,
          unitCost,
          received: 0,
          available: 0,
          reserved: 0,
          consumed: 0,
          returned: 0,
          onHand: 0,
          remainingValue: 0,
          lines: [],
        });
      }
      const group = grouped.get(key);
      group.received += Number(batch.quantity_received || 0);
      group.available += Number(batch.quantity_available || 0);
      group.reserved += Number(batch.quantity_reserved || 0);
      group.consumed += Number(batch.quantity_consumed || 0);
      group.returned += Number(batch.quantity_returned || 0);
      group.onHand += Number(batch.quantity_on_hand ?? ((batch.quantity_available || 0) + (batch.quantity_reserved || 0)));
      group.remainingValue += Number(batch.remaining_value ?? ((batch.quantity_available || 0) + (batch.quantity_reserved || 0)) * unitCost);
      group.lines.push(batch);
    }
    return Array.from(grouped.values());
  }, [batches]);

  const activeBatchGroups = useMemo(() => batchGroups.filter((group) => group.onHand > 0), [batchGroups]);
  const closedBatchGroups = useMemo(() => batchGroups.filter((group) => group.onHand <= 0), [batchGroups]);
  const visibleBatchGroups = showBatchHistory ? batchGroups : activeBatchGroups;

  const toggleBatchGroup = (groupId) => {
    setExpandedBatchGroups((current) => ({ ...current, [groupId]: !current[groupId] }));
  };

  const fetchData = async () => {
    try {
      const [movementsRes, valuationRes, lowStockRes, productsRes, batchesRes, reconciliationRes] = await Promise.all([
        inventoryAPI.getMovements(),
        inventoryAPI.getValuation(),
        inventoryAPI.getLowStock(),
        productsAPI.getAll({ all: true }),
        api.get('/inventory/batches'),
        api.get('/inventory/batches/reconcile'),
      ]);
      setMovements(movementsRes.data);
      setValuation(valuationRes.data);
      setLowStock(lowStockRes.data);
      setProducts(productsRes.data);
      setBatches(batchesRes.data || []);
      setReconciliation(reconciliationRes.data || null);
    } catch (error) {
      toast.error('Failed to fetch inventory data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await inventoryAPI.createMovement({
        ...formData,
        variation_id: formData.movement_type === 'promotion' ? (formData.variation_id || null) : null,
        reference_id: formData.movement_type === 'promotion' ? writeoffReference : null,
        quantity: parseInt(formData.quantity),
        unit_cost: formData.movement_type === 'in' ? Number(formData.unit_cost) : null,
      });
      toast.success(formData.movement_type === 'promotion' ? 'Promotion write-off recorded at FIFO cost' : 'Inventory movement recorded');
      setWriteoffReference(crypto.randomUUID());
      setDialogOpen(false);
      setFormData({ product_id: '', variation_id: '', movement_type: 'in', quantity: '', reason: '', unit_cost: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record movement');
    } finally {
      setSubmitting(false);
    }
  };

  const getMovementIcon = (type) => {
    switch (type) {
      case 'in':
        return <ArrowDownToLine className="w-4 h-4 text-emerald-600" />;
      case 'out':
        return <ArrowUpFromLine className="w-4 h-4 text-red-600" />;
      default:
        return <RefreshCw className="w-4 h-4 text-blue-600" />;
    }
  };

  const getMovementBadge = (type) => {
    switch (type) {
      case 'in':
        return <Badge className="badge-success">Stock In</Badge>;
      case 'out':
        return <Badge className="badge-error">Stock Out</Badge>;
      default:
        return <Badge className="badge-neutral">Adjustment</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Inventory
          </h2>
          <p className="text-slate-500 mt-1">Manage stock levels and movements</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="gap-2 bg-indigo-600 hover:bg-indigo-700" data-testid="add-movement-btn">
          <Plus className="w-4 h-4" />
          Add Movement
        </Button>
      </div>

      {/* Valuation Cards */}
      {valuation && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="stat-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Products</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{valuation.total_products}</p>
              </div>
              <div className="stat-icon bg-indigo-600">
                <Package className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Items</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{valuation.total_items.toLocaleString()}</p>
              </div>
              <div className="stat-icon bg-emerald-600">
                <Boxes className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Cost Value</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(valuation.total_cost_value)}</p>
              </div>
              <div className="stat-icon bg-blue-600">
                <ArrowDownToLine className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Retail Value</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(valuation.total_retail_value)}</p>
              </div>
              <div className="stat-icon bg-violet-600">
                <ArrowUpFromLine className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>
        </div>
      )}

      <Tabs defaultValue="movements" className="space-y-4">
        <TabsList>
          <TabsTrigger value="movements" data-testid="movements-tab">Movements</TabsTrigger>
          <TabsTrigger value="batches" data-testid="batches-tab">FIFO Stock ({activeBatchGroups.length})</TabsTrigger>
          <TabsTrigger value="low-stock" data-testid="low-stock-tab">
            Low Stock ({lowStock.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="movements">
          <Card>
            <CardContent className="p-0">
              {movements.length === 0 ? (
                <div className="text-center py-16">
                  <Boxes className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900">No movements recorded</h3>
                  <p className="text-slate-500 mt-1">Stock movements will appear here.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead className="table-header-cell">Date</TableHead>
                      <TableHead className="table-header-cell">Product</TableHead>
                      <TableHead className="table-header-cell">Type</TableHead>
                      <TableHead className="table-header-cell text-right">Qty</TableHead>
                      <TableHead className="table-header-cell text-right">Before</TableHead>
                      <TableHead className="table-header-cell text-right">After</TableHead>
                      <TableHead className="table-header-cell">Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {movements.slice(0, 50).map((movement) => (
                      <TableRow key={movement.id} className="table-row">
                        <TableCell className="table-cell text-slate-500">
                          {formatDate(movement.created_at)}
                        </TableCell>
                        <TableCell className="table-cell">
                          <div className="font-medium text-slate-900">{movement.display_name || movement.product_name || 'Product'}</div>
                          <div className="mt-0.5 text-xs text-slate-500">
                            {[movement.sku, movement.batch_number].filter(Boolean).join(' · ') || '—'}
                          </div>
                        </TableCell>
                        <TableCell className="table-cell">{movement.reference_type === 'promotion_writeoff' ? <Badge>Promotion / Photoshoot</Badge> : getMovementBadge(movement.movement_type)}</TableCell>
                        <TableCell className="table-cell text-right">
                          <span className={movement.movement_type === 'in' ? 'text-emerald-600' : movement.movement_type === 'out' ? 'text-red-600' : ''}>
                            {movement.movement_type === 'in' ? '+' : movement.movement_type === 'out' ? '-' : ''}
                            {movement.quantity}
                          </span>
                        </TableCell>
                        <TableCell className="table-cell text-right text-slate-500">{movement.previous_stock ?? '—'}</TableCell>
                        <TableCell className="table-cell text-right font-medium">{movement.new_stock ?? '—'}</TableCell>
                        <TableCell className="table-cell text-slate-500 max-w-sm">
                          <div>{movement.reason || 'Inventory movement'}</div>
                          {(movement.grn_number || movement.reference_type) && (
                            <div className="mt-0.5 text-xs text-slate-400">
                              {movement.grn_number || String(movement.reference_type || '').replaceAll('_', ' ')}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="batches">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <CardTitle>FIFO Stock Layers</CardTitle>
                  <p className="mt-1 text-sm text-slate-500">
                    One row is shown per product, GRN and cost. Expand a row to see the exact variation batches used by FIFO.
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    {batches.length} exact batch record(s) grouped into {batchGroups.length} stock layer(s).
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {closedBatchGroups.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => setShowBatchHistory((value) => !value)}
                    >
                      <History className="h-4 w-4" />
                      {showBatchHistory ? 'Hide closed history' : `Show closed history (${closedBatchGroups.length})`}
                    </Button>
                  )}
                  <Badge className={reconciliation?.is_reconciled ? 'badge-success' : 'badge-error'}>
                    {reconciliation?.is_reconciled ? 'Stock reconciled' : `${reconciliation?.count || 0} discrepancy(s)`}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {visibleBatchGroups.length === 0 ? (
                <div className="py-16 text-center text-slate-500">
                  {batches.length === 0
                    ? 'No inventory batches yet. Confirm a GRN or record a costed stock-in.'
                    : 'No active FIFO stock remains. Use “Show closed history” to view returned or consumed batches.'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead className="w-10" />
                        <TableHead>GRN / Source</TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead>Received</TableHead>
                        <TableHead className="text-right">Received Qty</TableHead>
                        <TableHead className="text-right">On Hand</TableHead>
                        <TableHead className="text-right">Reserved</TableHead>
                        <TableHead className="text-right">Returned</TableHead>
                        <TableHead className="text-right">Unit Cost</TableHead>
                        <TableHead className="text-right">Remaining Value</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleBatchGroups.map((group) => {
                        const expanded = Boolean(expandedBatchGroups[group.id]);
                        const status = batchStatus(group);
                        const variationCount = new Set(group.lines.map((line) => line.variation_id).filter(Boolean)).size;
                        return (
                          <React.Fragment key={group.id}>
                            <TableRow className="table-row">
                              <TableCell>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => toggleBatchGroup(group.id)}
                                  aria-label={expanded ? 'Collapse batch details' : 'Expand batch details'}
                                >
                                  {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </Button>
                              </TableCell>
                              <TableCell>
                                <div className="font-medium text-slate-900">{group.grnNumber}</div>
                                <div className="mt-0.5 text-xs text-slate-500">
                                  {group.lines.length} exact batch{group.lines.length === 1 ? '' : 'es'}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="font-medium text-slate-900">{group.productName}</div>
                                <div className="mt-0.5 text-xs text-slate-500">
                                  {variationCount > 0 ? `${variationCount} variation${variationCount === 1 ? '' : 's'}` : (group.lines[0]?.sku || 'Simple product')}
                                </div>
                              </TableCell>
                              <TableCell>{formatDate(group.receivedDate)}</TableCell>
                              <TableCell className="text-right">{group.received}</TableCell>
                              <TableCell className="text-right font-semibold text-slate-900">{group.onHand}</TableCell>
                              <TableCell className="text-right">{group.reserved}</TableCell>
                              <TableCell className="text-right">{group.returned}</TableCell>
                              <TableCell className="text-right">{formatCurrency(group.unitCost)}</TableCell>
                              <TableCell className="text-right font-medium">{formatCurrency(group.remainingValue)}</TableCell>
                              <TableCell><Badge className={batchStatusClass(status)}>{status}</Badge></TableCell>
                            </TableRow>
                            {expanded && group.lines.map((batch) => (
                              <TableRow key={batch.id} className="bg-slate-50/70">
                                <TableCell />
                                <TableCell className="pl-6 text-xs font-medium text-slate-600">{batch.batch_number}</TableCell>
                                <TableCell>
                                  <div className="text-sm text-slate-700">{batch.variation_label || 'Base product'}</div>
                                  <div className="mt-0.5 text-xs text-slate-400">{batch.sku || '—'}</div>
                                </TableCell>
                                <TableCell className="text-sm text-slate-500">{formatDate(batch.received_date)}</TableCell>
                                <TableCell className="text-right text-sm">{batch.quantity_received || 0}</TableCell>
                                <TableCell className="text-right text-sm font-medium">{batch.quantity_on_hand ?? ((batch.quantity_available || 0) + (batch.quantity_reserved || 0))}</TableCell>
                                <TableCell className="text-right text-sm">{batch.quantity_reserved || 0}</TableCell>
                                <TableCell className="text-right text-sm">{batch.quantity_returned || 0}</TableCell>
                                <TableCell className="text-right text-sm">{formatCurrency(batch.unit_cost)}</TableCell>
                                <TableCell className="text-right text-sm">{formatCurrency(batch.remaining_value || 0)}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="capitalize">
                                    {String(batch.status || 'open').replaceAll('_', ' ')}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="low-stock">
          <Card>
            <CardContent className="p-0">
              {lowStock.length === 0 ? (
                <div className="text-center py-16">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900">All stocked up!</h3>
                  <p className="text-slate-500 mt-1">No products are running low.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead className="table-header-cell">Product</TableHead>
                      <TableHead className="table-header-cell">SKU</TableHead>
                      <TableHead className="table-header-cell">Category</TableHead>
                      <TableHead className="table-header-cell text-right">Current Stock</TableHead>
                      <TableHead className="table-header-cell text-right">Threshold</TableHead>
                      <TableHead className="table-header-cell">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lowStock.map((product) => (
                      <TableRow key={product.id} className="table-row">
                        <TableCell className="table-cell font-medium">{product.name}</TableCell>
                        <TableCell className="table-cell text-slate-500">{product.sku}</TableCell>
                        <TableCell className="table-cell">{product.category || '-'}</TableCell>
                        <TableCell className="table-cell text-right">
                          <span className="text-amber-600 font-medium">{product.stock_quantity}</span>
                        </TableCell>
                        <TableCell className="table-cell text-right text-slate-500">{product.low_stock_threshold}</TableCell>
                        <TableCell className="table-cell">
                          {product.stock_quantity === 0 ? (
                            <Badge className="badge-error">Out of Stock</Badge>
                          ) : (
                            <Badge className="badge-warning">Low Stock</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Movement Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="movement-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Add Inventory Movement</DialogTitle>
            <DialogDescription>Stock-in creates a FIFO cost batch. Stock-out consumes the oldest available batches.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Product *</Label>
                <Select value={formData.product_id} onValueChange={(v) => setFormData({ ...formData, product_id: v, variation_id: '' })}>
                  <SelectTrigger data-testid="select-movement-product">
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} (Stock: {p.stock_quantity})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Movement Type *</Label>
                <Select value={formData.movement_type} onValueChange={(v) => setFormData({ ...formData, movement_type: v })}>
                  <SelectTrigger data-testid="select-movement-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Stock In (Add)</SelectItem>
                    <SelectItem value="out">Stock Out (Remove)</SelectItem>
                    <SelectItem value="promotion">Promotion / Photoshoot (Write-off)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.movement_type === 'promotion' && (
                <>
                  <p className="text-sm text-muted-foreground">Permanently remove unsellable promotional stock. Its FIFO cost is charged to Promotional Samples expense and deducted from inventory value. No cash payment is recorded.</p>
                  {selectedProduct?.product_type === 'variable' && (
                    <div className="space-y-2">
                      <Label>Size / Variation *</Label>
                      <Select value={formData.variation_id} onValueChange={(v) => setFormData({ ...formData, variation_id: v })} disabled={variationsLoading}>
                        <SelectTrigger><SelectValue placeholder={variationsLoading ? 'Loading sizes...' : 'Select exact size / variation'} /></SelectTrigger>
                        <SelectContent>{variations.map((v) => <SelectItem key={v.id} value={v.id}>{v.variation_name || v.name || v.sku} (Stock: {v.stock_quantity})</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}
              <div className="space-y-2">
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  required
                  data-testid="movement-quantity"
                />
              </div>
              {formData.movement_type === 'in' && (
                <div className="space-y-2">
                  <Label>Unit Cost (LKR) *</Label>
                  <Input type="number" min="0" step="0.01" value={formData.unit_cost} onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })} required data-testid="movement-unit-cost" />
                  <p className="text-xs text-slate-500">A new FIFO inventory batch will be created at this exact cost.</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Reason *</Label>
                <Input
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="e.g., Manual count adjustment"
                  required
                  data-testid="movement-reason"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting || !formData.product_id || (formData.movement_type === 'promotion' && (variationsLoading || (selectedProduct?.product_type === 'variable' && !formData.variation_id))) || (formData.movement_type === 'in' && formData.unit_cost === '')} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-movement-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Record Movement
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inventory;
